import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { apiGetOffline } from "../lib/offline";
import { useAuth } from "../context/AuthContext";
import { withNext } from "../utils/authRedirect";
import { useSetHelpMessage } from "../context/HelpContext";
import Spinner from "../components/Spinner";
import OfflineImage from "../components/OfflineImage";
import RichTextRenderer from "../components/RichTextRenderer";
import SEOHead from "../components/SEOHead";
import { CheckCircle2, BookOpen, Lock, Clock, Users, Award, ChevronDown, ChevronUp, FileText, ListChecks, PlayCircle } from "lucide-react";

export default function CourseDetail() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || localStorage.getItem("sf_ref") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState(false);
  const [openModule, setOpenModule] = useState(0);

  useEffect(() => {
    let mounted = true;
    const loadCourse = async () => {
      try {
        const { data } = await apiGetOffline(`/courses/${courseId}`);
        if (!mounted) return;
        setCourse(data);
        if (user) {
          const enrollments = await apiGetOffline("/enrollments/my");
          if (!mounted) return;
          const enrollment = enrollments.data.find((e) => e.course?.course_id === data.course_id || e.course?.slug === data.slug);
          setEnrolled(Boolean(enrollment));
          if (enrollment?.course) setCourse(enrollment.course);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCourse();
    return () => { mounted = false; };
  }, [courseId, user]);

  useSetHelpMessage(
    course ? `Bonjour ! J'ai une question sur la formation "${course.title}" (${course.price_eur}€).` : null,
    [course?.title, course?.price_eur]
  );

  const totalLessons = (course?.modules || []).reduce((s, m) => s + (m.lessons || []).length, 0);

  const handleBuy = () => {
    const checkoutPath = `/checkout/${course?.course_id || courseId}${ref ? `?ref=${ref}` : ""}`;
    // Sans compte : on envoie vers l'inscription en gardant la commande en
    // memoire, pour reprendre le paiement juste apres (et non atterrir sur le
    // tableau de bord en ayant perdu l'achat).
    if (!user) { navigate(withNext("/register", checkoutPath)); return; }
    navigate(checkoutPath);
  };

  const handleStart = () => {
    const first = (course?.modules || []).flatMap((m) => m.lessons || [])[0];
    if (first) navigate(`/learn/${first.lesson_id}`);
  };

  if (loading) return <Spinner className="min-h-[60vh]" />;
  if (!course) return <p className="text-center mt-16">Formation introuvable.</p>;

  const courseImageAlt = course.image_alt || course.seo_subtitle || course.seo_title || course.title || "Formation SF Learning";
  const courseDescription = course.seo_description || course.seo_subtitle || course.short_description?.replace(/<[^>]+>/g, "") || "Formation SF Learning";

  return (
    <div data-testid="course-detail-page">
      <SEOHead
        title={course.seo_title || `${course.title} — SF Learning`}
        description={courseDescription}
        image={course.cover_image}
        imageAlt={courseImageAlt}
        url={typeof window !== "undefined" ? window.location.href : ""}
        type="product"
        modifiedTime={course.updated_at}
        author="SF Learning"
      />
      <section className="bg-white border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            {course.category && <span className="badge orange mb-3">{course.category}</span>}
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">{course.title}</h1>
            {course.seo_subtitle && <p className="text-xl font-semibold text-brand-green mt-4">{course.seo_subtitle}</p>}
            <RichTextRenderer html={course.short_description} className="text-lg text-gray-700 mt-4" />
            <div className="mt-6 flex items-center gap-6 text-sm text-gray-600 flex-wrap">
              <span className="inline-flex items-center gap-1"><BookOpen className="w-4 h-4 text-brand-green" /> {course.modules?.length || 0} modules</span>
              <span className="inline-flex items-center gap-1"><PlayCircle className="w-4 h-4 text-brand-green" /> {totalLessons} leçons</span>
              <span className="inline-flex items-center gap-1"><Award className="w-4 h-4 text-brand-green" /> Devoirs corrigés</span>
              <span className="inline-flex items-center gap-1"><Users className="w-4 h-4 text-brand-green" /> Communauté incluse</span>
              {course.drip_enabled && <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4 text-brand-orange" /> 1 module / {course.drip_interval_days || 7} jours</span>}
            </div>
            <div className="mt-8 prose-sf max-w-none">
              <h2>À propos de cette formation</h2>
              <RichTextRenderer html={course.long_description} />
            </div>

            <div className="mt-10">
              <h2 className="font-heading text-2xl font-bold mb-4 text-brand-green">Programme</h2>
              <div className="space-y-3">
                {(course.modules || []).map((m, idx) => (
                  <div key={m.module_id} className="card overflow-hidden">
                    <button className="w-full flex items-center justify-between px-5 py-4 hover:bg-emerald-50" onClick={() => setOpenModule(openModule === idx ? -1 : idx)} data-testid={`module-toggle-${idx}`}>
                      <span className="flex items-center gap-3">
                        <span className="badge">{idx + 1}</span>
                        <span className="font-heading font-bold text-left flex items-center gap-2">
                          {m.locked && <Lock className="w-4 h-4 text-gray-400" />}
                          <span>{m.title}</span>
                          {m.locked && <span className="text-xs font-medium text-gray-500">Disponible dans {m.days_until_unlock || 1} jour{(m.days_until_unlock || 1) > 1 ? "s" : ""}</span>}
                        </span>
                      </span>
                      {openModule === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    {openModule === idx && (
                      <div className="border-t border-emerald-100 px-5 py-4 space-y-2">
                        {(m.lessons || []).map((l) => (
                          <div key={l.lesson_id} className="flex items-center gap-2 text-sm">
                            {l.type === "assignment" ? <FileText className="w-4 h-4 text-brand-orange" /> : l.type === "quiz" ? <ListChecks className="w-4 h-4 text-brand-orange" /> : <PlayCircle className="w-4 h-4 text-brand-green" />}
                            <span>{l.title}</span>
                            {(!enrolled || m.locked) && <Lock className="w-3.5 h-3.5 text-gray-400 ml-auto" />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:sticky lg:top-[100px] self-start">
            <div className="card overflow-hidden">
              {course.cover_image && <OfflineImage src={course.cover_image} alt={courseImageAlt} className="w-full aspect-video object-cover" />}
              <div className="p-6">
                <p className="text-sm text-gray-500">À partir de</p>
                <p className="font-heading text-4xl font-black text-brand-green">{course.price_eur}€</p>
                {enrolled ? (
                  <button onClick={handleStart} className="btn-primary w-full justify-center mt-4" data-testid="continue-course-btn">
                    Continuer la formation
                  </button>
                ) : (
                  <button onClick={handleBuy} className="btn-primary w-full justify-center mt-4" data-testid="buy-course-btn">
                    Acheter maintenant
                  </button>
                )}
                <ul className="mt-5 space-y-2 text-sm">
                  {["Accès à vie aux contenus", "Devoirs corrigés sous 48h", "Accès à la communauté", "Mise à jour régulière"].map((t, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-green mt-0.5" /> {t}</li>
                  ))}
                </ul>
                {ref && (
                  <p className="text-xs text-amber-700 mt-3 bg-amber-50 p-2 rounded">Recommandé par <strong>{ref}</strong></p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
